import React, { useState, useEffect } from "react";

// Personal Information
const PERSONAL_INFO = {
  name: "Taylor Thomas",
  title: "E-Learning Developer & Instructional Designer",
  description:
    "Transforming complex concepts into engaging, measurable learning experiences that drive real behavior change and organizational success.",
  linkedin: "https://www.linkedin.com/in/taylor-t-476353291/",
};

// Featured Projects
const PROJECTS = [
  {
    id: 1,
    title: "Travel Agency Success Fundamentals",
    description:
      "Comprehensive e-learning module designed to equip new travel agents with essential skills, protocols, and best practices for client success and business growth.",
    badge: "Capstone",
    image: "/images/airplane.jpg",
    tools: ["Articulate Storyline", "Vyond Animation", "OpenAI", "Krita"],
    projectLink:
      "https://taylorthomasstoryline.s3.us-east-2.amazonaws.com/TASF/story.html",
    gallery: [
      "/images/Travel gallery 1.png",
      "/images/Travel Gallery 2.png",
      "/images/Travel gallery3.png",
      "/images/Travel Gallery 4.png",
      "/images/Travel Gallery 5.png",
      "/images/Traveltrainingss.png",
    ],
    detailedDescription: `
      <h3>Project Overview</h3>
      <p>This capstone project brings together everything I’ve learned in my instructional design program. I created a self-paced online course for new and aspiring travel advisors that covers foundational industry knowledge, client communication, and itinerary planning. The course uses scenario-based learning and AI-powered customer interactions to simulate real-world challenges in a travel advisor’s day-to-day. I designed and developed the full experience using Articulate Storyline 360, Vyond for animated explainers, Clipchamp for voiceover narration, Krita for custom graphics, and OpenAI to power the dynamic customer simulations.</p>
      <h3>Learning Objectives</h3>
      <ul>
        <li>Analyze the interdependent relationships between key stakeholders in the global travel ecosystem (travel agencies, airlines, hotels, and vendors)</li>
        <li>Categorize major global travel destinations according to their geographic regions, cultural significance, and primary tourist attractions</li>
        <li>Demonstrate effective customer service strategies in various client scenarios to achieve positive resolution outcomes</li>
        <li>Apply problem-solving techniques to analyze customer complaints and develop appropriate solutions</li>
        <li>Evaluate travel itineraries based on completeness, balance, special considerations, and cultural appropriateness to identify well-constructed versus poorly-written travel plans</li>
        <li>Create comprehensive client-specific travel itineraries that include accommodations, transportation, balanced scheduling, cultural guidance, and emergency information</li>
      </ul>
    `,
  },
  {
    id: 2,
    title: "Lab Safety Training Module",
    description:
      "Interactive Storyline demo combining constructivist and behaviorist learning theories. Features drag-and-drop activities, sequencing exercises, and immediate feedback mechanisms.",
    badge: "Interactive Demo",
    image: "/images/Labsafetymodulethumbnail.png",
    tools: [
      "Articulate Storyline 360",
      "Interactive Activities",
      "Assessment Tools",
    ],
    projectLink: "http://labsafetytraining.s3-website.us-east-2.amazonaws.com/",
    gallery: [
      "/images/Labsafetymodulethumbnail.png",
      "/images/Labgall1.png",
      "/images/labgall2.png",
      "/images/Labgall3.png",
      "/images/Labgall4.png",
    ],
    detailedDescription: `
      <h3>Project Overview</h3>
      <p>This lab safety training module was an independent project I created while experimenting with Articulate Storyline 360 for the first time. Inspired by real procedures in a hematology lab, the goal was to simulate a realistic training experience while testing out different interactive features. I incorporated drag-and-drop activities, sequencing challenges, and instant feedback to mimic a hands-on environment. The module blends behaviorist strategies (like reinforcement through feedback) with constructivist elements that let users learn through exploration and simulated decision-making.</p>
      <h3>Learning Objectives</h3>
      <ul>
        <li>Demonstrate correct chemical storage practices using drag-and-drop interaction</li>
        <li>Select appropriate procedures for needle disposal through multiple-choice questions</li>
        <li>Sequence the steps for proper cleanup following lab protocols</li>
        <li>Identify suitable personal protective equipment (PPE) for various lab tasks</li>
      </ul>
    `,
  },
  {
    id: 3,
    title: "Cybersecurity Haunted House Escape Room",
    description:
      "Immersive Halloween-themed virtual escape room teaching corporate cybersecurity basics through gamification, multimedia elements, and progressive challenge design.",
    badge: "Game-Based Learning",
    image: "/images/HauntedHouseThumbnail.png",
    tools: [
      "Google Slides",
      "Google Forms",
      "Game Design",
      "Audio Integration",
    ],
    projectLink:
      "https://docs.google.com/presentation/d/e/2PACX-1vTOrKUC6AMWmYcMdVnigjp6fYgNCQXsXlJe3R3-8mwHAZrYtzfbvKiiDINvQXF6I2rZlLQWdefp7TLV/pub?start=false&loop=false&delayms=3000",
    gallery: [
      "/images/HauntedHouseThumbnail.png",
      "/images/Hauntedgall1.png",
      "/images/Hauntedgall2.png",
      "/images/Hauntedgall3.png",
    ],
    detailedDescription: `
      <h3>Project Overview</h3>
      <p> This haunted house-themed virtual escape room was created near Halloween for a graduate-level course on Game-Based Learning. Aimed at corporate employees, the module introduces basic cybersecurity concepts in a fun, seasonal format. Built using Google Slides and Google Forms, the escape room features spooky audio, themed visuals, and interactive challenges that test users on password strength, phishing awareness, and protocol basics.</p>
      <p> I handled the bulk of the design and development—selecting all the graphics and sound effects, assembling the Google Form, embedding questions and hints into the slide deck, and curating the multimedia that supports each interaction. My partner contributed by helping select a few visuals and assisting with some of the branching path configurations. The final experience is engaging, accessible, and reinforces key cybersecurity practices through a game-based structure.</p>
      <h3>Learning Objectives</h3>
      <ul>
        <li>Identify characteristics of secure vs. insecure passwords</li>
        <li>Recognize red flags in phishing emails</li>
        <li>Apply basic cybersecurity practices in simulated workplace scenarios</li>
      </ul>
    `,
  },
  {
    id: 4,
    title: "Game Based Teaching Toolkit",
    description:
      "This e-book presents a curated collection of game-based learning (GBL) tools tailored for corporate training and development.",
    badge: "Portfolio Sample",
    image: "/images/GBLtookitthumbnail.png",
    tools: ["Canva", "Microlearning", "Adobe Illustrator", "Resource"],
    projectLink: "https://heyzine.com/flip-book/dd9d4b7435.html",
    embedLink: "https://heyzine.com/flip-book/dd9d4b7435.html",
    gallery: ["/images/GBLtookitthumbnail.png"],
    detailedDescription: `
      <h3>About This Toolkit</h3>
      <p>This comprehensive e-book serves as a practical guide for instructional designers and L&D professionals looking to incorporate game-based learning into their corporate training programs.</p>
      <h3>What You'll Find Inside:</h3>
      <ul>
        <li>Curated collection of proven GBL tools and platforms</li>
        <li>Implementation strategies for corporate environments</li>
        <li>Case studies and real-world applications</li>
        <li>Design principles for effective game-based training</li>
        <li>Engagement and assessment strategies</li>
      </ul>
      <p>Browse the interactive e-book below to explore the full toolkit.</p>
    `,
  },
];

// Skills/Expertise
const EXPERTISE = [
  {
    title: "Learning Design",
    description:
      "ADDIE, SAM, Constructivism, Behaviorism, Game-Based Learning, Microlearning Strategies",
  },
  {
    title: "Authoring Tools",
    description:
      "Articulate Storyline 360, Articulate Rise, Adobe Captivate, Vyond Animation",
  },
  {
    title: "Design & Development",
    description:
      "Adobe Creative Suite, Krita, Interactive Activities, Assessment Design",
  },
  {
    title: "Emerging Technologies",
    description:
      "AI Integration (OpenAI), Virtual Reality Applications, Adaptive Learning Systems",
  },
];

// Other Professional Work
const OTHER_WORK = [
  {
    id: 1,
    title: "Three Demands of Cognitive Capacity",
    type: "infographic",
    description:
      "A visual breakdown of the three types of mental processing involved in learning: Extraneous, Essential, and Generative. This infographic was created to help instructional designers quickly reference how different content demands different levels of cognitive effort.",
    image: "/images/CLthumbnail.png",
    link: "https://www.canva.com/design/DAGHnlea6Ew/WrWH0tn9KhK7h3nvHVFPrQ/view?utm_content=DAGHnlea6Ew&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h8d1c52e549",
    category: "Visual Design",
  },
  {
    id: 2,
    title: "Instructional Videos",
    type: "video",
    description:
      "A curated collection of instructional videos created for both corporate and academic settings. These include technical tutorials for IT onboarding and animated e-learning content developed with Vyond. Each video applies Mayer’s Multimedia Principles—especially signaling, coherence, and segmenting—to support clarity and reduce cognitive load.",
    image: "/images/VideoIconpf.png",
    link: "https://www.youtube.com/@taylorthomas1961",
    category: "Multimedia Learning",
  },
  {
    id: 3,
    title: "Spirit Airlines Simulation Storyboard",
    type: "document",
    description:
      "Storyboard for a gamified onboarding simulation developed in my GBL course. Focuses on emergency response, customer service, and safety checks using branching scenarios and immediate feedback, grounded in behaviorist and experiential learning theories.",
    image: "/images/storyboarda.png",
    link: "https://www.canva.com/design/DAGZCPuTSyM/JOPkZIM7Yj20vhFnvnHvPg/view?utm_content=DAGZCPuTSyM&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h3c3b338032",
    category: "Strategy & Planning",
  },
  {
    id: 4,
    title: "Interactive 3D Travel Globe",
    type: "infographic",
    description:
      "I designed and coded this interactive globe for my instructional design capstone to help travel advisors explore global destinations. Built with React and Three.js, it supports visual and exploratory learning styles by allowing users to rotate the globe and access key destination details through interactive popups—making geographic information more accessible and engaging.",
    image: "/images/3Dglobe.png",
    link: "https://genuine-lamington-df1d91.netlify.app/",
    category: "Multimedia Learning",
  },
  {
    id: 5,
    title: "Instructional Design Strategy Document",
    type: "document",
    description:
      "Comprehensive strategy document outlining approach to corporate training redesign. Includes needs analysis methodology, learning theory applications, and implementation timelines for large-scale training transformations.",
    image: "/images/stay tuned.png",
    link: "https://drive.google.com/file/d/YOUR-DOC-ID/view",
    category: "Strategy & Planning",
  },
];

// Colors
const COLORS = {
  primary: "#6b705c",
  secondary: "#8b9556",
  background: "#faf9f6",
  text: "#3a3630",
  textLight: "#6b705c",
};

// Mobile-First Responsive Styles
const getStyles = (isMobile) => ({
  container: {
    minHeight: "100vh",
    backgroundColor: COLORS.background,
    color: COLORS.text,
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: 1.6,
    overflowX: "hidden",
  },
  header: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
    color: "white",
    padding: isMobile ? "3rem 1rem 4rem 1rem" : "5rem 0",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  headerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: isMobile ? "0 1rem" : "0 2rem",
    position: "relative",
    zIndex: 2,
  },
  mainTitle: {
    fontSize: isMobile ? "2rem" : "4rem",
    fontWeight: 200,
    marginBottom: "1rem",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: isMobile ? "1.1rem" : "1.5rem",
    marginBottom: "1.5rem",
    opacity: 0.95,
    fontWeight: 400,
  },
  description: {
    fontSize: isMobile ? "1rem" : "1.2rem",
    opacity: 0.9,
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: 1.6,
  },
  lineSeparator: {
    width: "80px",
    height: "3px",
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    margin: "2rem auto",
    borderRadius: "2px",
  },
  nav: {
    background: "white",
    padding: "1rem 0",
    boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    borderBottom: "1px solid #e5ddd5",
  },
  navContainer: {
    display: "flex",
    justifyContent: isMobile ? "space-between" : "center",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1rem",
    position: "relative",
  },
  mobileMenuButton: {
    display: isMobile ? "block" : "none",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    color: COLORS.text,
    cursor: "pointer",
    padding: "0.5rem",
    borderRadius: "4px",
    transition: "background-color 0.3s ease",
  },
  navLinks: {
    display: isMobile ? "none" : "flex",
    gap: isMobile ? "0" : "3rem",
  },
  navLinksMobile: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "white",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    padding: "0",
    gap: "0",
    zIndex: 50,
  },
  navLink: {
    color: COLORS.text,
    textDecoration: "none",
    fontWeight: 500,
    padding: isMobile ? "1rem 1.5rem" : "0.75rem 1.5rem",
    borderRadius: isMobile ? 0 : "8px",
    transition: "all 0.3s ease",
    cursor: "pointer",
    borderBottom: isMobile ? "1px solid #f0f0f0" : "none",
    display: "block",
  },
  main: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: isMobile ? "2rem 1rem" : "4rem 2rem",
  },
  portfolioSection: {
    marginBottom: isMobile ? "4rem" : "6rem",
    position: "relative",
  },
  portfolioHeader: {
    textAlign: "center",
    marginBottom: isMobile ? "3rem" : "5rem",
  },
  sectionTitle: {
    fontSize: isMobile ? "1.8rem" : "3rem",
    color: COLORS.text,
    fontWeight: 200,
    marginBottom: "1.5rem",
  },
  sectionDescription: {
    color: COLORS.textLight,
    fontSize: isMobile ? "1rem" : "1.3rem",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: 1.7,
    padding: isMobile ? "0 1rem" : "0",
  },
  portfolioGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
    gap: isMobile ? "2rem" : "3rem",
    marginTop: isMobile ? "3rem" : "4rem",
  },
  portfolioItem: {
    background: "white",
    overflow: "hidden",
    transition: "all 0.4s ease",
    position: "relative",
    cursor: "pointer",
    borderRadius: "20px",
    boxShadow: "0 6px 25px rgba(0,0,0,0.08)",
  },
  portfolioImage: {
    position: "relative",
    height: isMobile ? "220px" : "280px",
    overflow: "hidden",
    background: "#f5f3f0",
  },
  portfolioImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.4s ease",
  },
  portfolioTitleOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)",
    padding: "1.5rem",
    zIndex: 1,
  },
  portfolioTitleText: {
    color: "white",
    fontSize: isMobile ? "1.2rem" : "1.4rem",
    fontWeight: 600,
    margin: 0,
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  portfolioOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: `linear-gradient(135deg, rgba(107,112,92,0.96) 0%, rgba(139,149,86,0.96) 100%)`,
    color: "white",
    padding: "1.5rem",
    transition: "transform 0.4s ease",
    zIndex: 2,
    transform: isMobile ? "translateY(0)" : "translateY(100%)",
  },
  overlayContent: {
    marginBottom: "1rem",
  },
  overlayDescription: {
    fontSize: "0.9rem",
    opacity: 0.95,
    lineHeight: 1.4,
    margin: 0,
  },
  toolsUsed: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.6rem",
  },
  toolTag: {
    background: "rgba(255,255,255,0.25)",
    padding: "0.4rem 0.8rem",
    borderRadius: "15px",
    fontSize: "0.75rem",
    fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.3)",
  },
  aboutSection: {
    background: "white",
    padding: isMobile ? "3rem 1rem" : "6rem 0",
    marginTop: isMobile ? "3rem" : "4rem",
    borderRadius: isMobile ? "20px" : "30px 30px 5px 30px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
    position: "relative",
    overflow: "hidden",
  },
  aboutContent: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1.8fr",
    gap: isMobile ? "2rem" : "4rem",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    maxWidth: "1100px",
    margin: "0 auto",
    padding: isMobile ? "0" : "0 2rem",
  },
  aboutText: {
    order: isMobile ? 2 : 1,
  },
  aboutParagraph: {
    color: COLORS.textLight,
    fontSize: isMobile ? "1rem" : "1.2rem",
    marginBottom: isMobile ? "1.5rem" : "2rem",
    lineHeight: 1.7,
  },
  aboutImage: {
    borderRadius: isMobile ? "15px" : "20px 5px 20px 20px",
    overflow: "hidden",
    boxShadow: "0 15px 50px rgba(0,0,0,0.12)",
    position: "relative",
    order: isMobile ? 1 : 2,
  },
  aboutImg: {
    width: "100%",
    height: isMobile ? "300px" : "450px",
    objectFit: "cover",
  },
  expertiseGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
    gap: isMobile ? "1.5rem" : "2rem",
    marginTop: isMobile ? "3rem" : "4rem",
    maxWidth: "1100px",
    margin: isMobile ? "3rem auto 0" : "4rem auto 0",
    padding: isMobile ? "0" : "0 2rem",
  },
  expertiseItem: {
    background: "#f8f6f2",
    padding: isMobile ? "1.5rem" : "2rem",
    borderRadius: "15px 15px 15px 3px",
    transition: "all 0.3s ease",
    borderLeft: "4px solid transparent",
  },
  expertiseTitle: {
    fontSize: isMobile ? "1.1rem" : "1.3rem",
    marginBottom: "1rem",
    fontWeight: 600,
    color: COLORS.text,
  },
  expertiseDescription: {
    color: COLORS.textLight,
    fontSize: isMobile ? "0.9rem" : "1rem",
    lineHeight: 1.6,
  },
  contactSection: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
    color: "white",
    padding: isMobile ? "3rem 1rem" : "6rem 0",
    textAlign: "center",
    borderRadius: isMobile ? "15px" : "5px 30px 30px 30px",
    marginTop: isMobile ? "3rem" : "4rem",
    position: "relative",
    overflow: "hidden",
  },
  contactContent: {
    position: "relative",
    zIndex: 1,
    maxWidth: "1200px",
    margin: "0 auto",
    padding: isMobile ? "0" : "0 2rem",
  },
  contactTitle: {
    fontSize: isMobile ? "1.8rem" : "2.8rem",
    marginBottom: "1.5rem",
    fontWeight: 200,
  },
  contactDescription: {
    fontSize: isMobile ? "1rem" : "1.3rem",
    marginBottom: isMobile ? "2rem" : "3rem",
    opacity: 0.95,
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: 1.6,
  },
  contactButton: {
    display: "inline-block",
    background: "white",
    color: COLORS.primary,
    padding: isMobile ? "1rem 2rem" : "1.3rem 3rem",
    borderRadius: "50px",
    border: "none",
    fontWeight: 600,
    fontSize: isMobile ? "1rem" : "1.1rem",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  projectDetail: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: isMobile ? "1rem" : "2rem",
    background: "white",
    minHeight: "100vh",
  },
  closeButton: {
    background: COLORS.primary,
    color: "white",
    border: "none",
    padding: isMobile ? "0.8rem 1.5rem" : "1rem 2rem",
    borderRadius: "8px",
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: "2rem",
  },
  projectHeader: {
    marginBottom: "2rem",
  },
  projectTitle: {
    fontSize: isMobile ? "1.8rem" : "2.5rem",
    color: COLORS.text,
    fontWeight: 300,
  },
  projectBadge: {
    display: "inline-block",
    padding: "0.5rem 1.2rem",
    borderRadius: "20px",
    color: "white",
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "1rem",
  },
  projectImage: {
    width: "100%",
    height: isMobile ? "250px" : "400px",
    objectFit: "cover",
    borderRadius: "16px",
    marginBottom: "2rem",
  },
  experienceButtonContainer: {
    textAlign: "center",
    marginBottom: "3rem",
  },
  experienceButton: {
    display: "inline-block",
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
    color: "white",
    padding: isMobile ? "1rem 2rem" : "1.2rem 2.5rem",
    borderRadius: "50px",
    textDecoration: "none",
    fontSize: isMobile ? "1rem" : "1.1rem",
    fontWeight: 600,
    transition: "all 0.3s ease",
  },
  embedContainer: {
    marginBottom: "3rem",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  },
  embedIframe: {
    border: "1px solid lightgray",
    width: "100%",
    height: isMobile ? "400px" : "600px",
    borderRadius: "16px",
  },
  projectTools: {
    marginBottom: "3rem",
  },
  toolsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.8rem",
    marginTop: "1rem",
  },
  projectToolTag: {
    background: "#f8f6f2",
    padding: isMobile ? "0.5rem 1rem" : "0.7rem 1.2rem",
    borderRadius: "20px",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: 500,
    color: COLORS.primary,
    border: `1px solid ${COLORS.primary}`,
  },
  projectContent: {
    color: COLORS.textLight,
    fontSize: isMobile ? "1rem" : "1.1rem",
    lineHeight: 1.8,
  },
  gallerySection: {
    marginTop: "4rem",
    paddingTop: "3rem",
    borderTop: "2px solid #f0f0f0",
  },
  galleryTitle: {
    fontSize: isMobile ? "1.4rem" : "1.8rem",
    color: COLORS.text,
    marginBottom: "2rem",
    fontWeight: 600,
  },
  gallery: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))",
    gap: isMobile ? "1rem" : "1.5rem",
  },
  galleryItem: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.3s ease",
  },
  galleryImage: {
    width: "100%",
    height: isMobile ? "200px" : "250px",
    objectFit: "cover",
    transition: "transform 0.3s ease",
  },
  lightboxOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "1rem",
  },
  lightboxContent: {
    position: "relative",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  lightboxImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: "8px",
  },
  lightboxClose: {
    position: "absolute",
    top: isMobile ? "-30px" : "-40px",
    right: isMobile ? "-15px" : "-40px",
    background: "white",
    border: "none",
    borderRadius: "50%",
    width: isMobile ? "30px" : "40px",
    height: isMobile ? "30px" : "40px",
    fontSize: isMobile ? "1.2rem" : "1.5rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.text,
  },
});

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Lightbox Component
const Lightbox = ({ image, onClose, isMobile }) => {
  const styles = getStyles(isMobile);
  
  return (
    <div style={styles.lightboxOverlay} onClick={onClose}>
      <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
        <button style={styles.lightboxClose} onClick={onClose}>
          ×
        </button>
        <img src={image} alt="Enlarged view" style={styles.lightboxImage} />
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onClick, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getStyles(isMobile);

  return (
    <div
      style={{
        ...styles.portfolioItem,
        transform: isHovered && !isMobile ? "translateY(-8px)" : "translateY(0)",
        boxShadow: isHovered && !isMobile
          ? "0 15px 40px rgba(0,0,0,0.12)"
          : "0 6px 25px rgba(0,0,0,0.08)",
      }}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onClick={onClick}
    >
      <div style={styles.portfolioImage}>
        <img
          src={project.image}
          alt={project.title}
          style={{
            ...styles.portfolioImg,
            transform: isHovered && !isMobile ? "scale(1.05)" : "scale(1)",
          }}
        />
        
        <div style={styles.portfolioTitleOverlay}>
          <h3 style={styles.portfolioTitleText}>{project.title}</h3>
        </div>
        
        <div
          style={{
            ...styles.portfolioOverlay,
            transform: isMobile || isHovered ? "translateY(0)" : "translateY(100%)",
          }}
        >
          <div style={styles.overlayContent}>
            <p style={styles.overlayDescription}>{project.description}</p>
          </div>
          <div style={styles.toolsUsed}>
            {project.tools.slice(0, 3).map((tool, i) => (
              <span key={i} style={styles.toolTag}>
                {tool}
              </span>
            ))}
            {project.tools.length > 3 && (
              <span style={styles.toolTag}>+{project.tools.length - 3}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Navigation Component
const MobileNavigation = ({ currentPage, onNavigate, mobileMenuOpen, setMobileMenuOpen, isMobile }) => {
  const styles = getStyles(isMobile);
  
  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.navContainer}>
        <button 
          style={{
            ...styles.mobileMenuButton,
            backgroundColor: mobileMenuOpen ? "#f0f0f0" : "transparent"
          }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? "✕" : "☰"}
        </button>
        
        <div style={{
          ...styles.navLinks,
          ...(mobileMenuOpen ? styles.navLinksMobile : {})
        }}>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              onNavigate("home");
            }} 
            style={{
              ...styles.navLink,
              backgroundColor: currentPage === "home" ? "#f0f0f0" : "transparent"
            }}
          >
            Home
          </a>
          <a
            href="#portfolio"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== "home") {
                onNavigate("home");
                setTimeout(() => scrollToSection("portfolio"), 100);
              } else {
                scrollToSection("portfolio");
              }
            }}
            style={styles.navLink}
          >
            Featured Projects
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("otherWork");
            }}
            style={{
              ...styles.navLink,
              backgroundColor: currentPage === "otherWork" ? "#f0f0f0" : "transparent"
            }}
          >
            Portfolio
          </a>
          <a
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== "home") {
                onNavigate("home");
                setTimeout(() => scrollToSection("about"), 100);
              } else {
                scrollToSection("about");
              }
            }}
            style={styles.navLink}
          >
            About
          </a>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== "home") {
                onNavigate("home");
                setTimeout(() => scrollToSection("contact"), 100);
              } else {
                scrollToSection("contact");
              }
            }}
            style={styles.navLink}
          >
            Contact
          </a>
        </div>
      </div>
    </nav>
  );
};

// Work Detail View Component
const WorkDetailView = ({ work, onClose, isMobile }) => {
  const styles = getStyles(isMobile);
  
  return (
    <div style={styles.projectDetail}>
      <button style={styles.closeButton} onClick={onClose}>
        ← Back to Portfolio
      </button>

      <div style={styles.projectHeader}>
        <h1 style={styles.projectTitle}>{work.title}</h1>
        <div
          style={{
            ...styles.projectBadge,
            background: work.type === "training-module" ? "#8b9556" : "#6b705c",
          }}
        >
          {work.type.toUpperCase().replace("-", " ")}
        </div>
      </div>

      <img src={work.image} alt={work.title} style={styles.projectImage} />

      <div style={styles.experienceButtonContainer}>
        <a
          href={work.link || "#"}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.experienceButton}
          onClick={(e) => {
            if (!work.link || work.link.includes("YOUR-")) {
              e.preventDefault();
              alert("Link coming soon!");
            }
          }}
        >
          {work.type === "video"
            ? "Watch Video"
            : work.type === "document"
            ? "View Document"
            : work.type === "presentation"
            ? "View Presentation"
            : work.type === "infographic"
            ? "View Full Size"
            : work.type === "training-module"
            ? "Access Training Module"
            : "View Work"}
        </a>
      </div>

      <div style={styles.projectTools}>
        <h3>Category:</h3>
        <div style={styles.toolsList}>
          <span style={styles.projectToolTag}>{work.category}</span>
        </div>
      </div>

      <div style={styles.projectContent}>
        <h3>About This Work</h3>
        <p>{work.description}</p>
      </div>
    </div>
  );
};

// Project Detail View Component
const ProjectDetailView = ({ project, onClose, onImageClick, isMobile }) => {
  const styles = getStyles(isMobile);
  
  return (
    <div style={styles.projectDetail}>
      <button style={styles.closeButton} onClick={onClose}>
        ← Back to Portfolio
      </button>

      <div style={styles.projectHeader}>
        <h1 style={styles.projectTitle}>{project.title}</h1>
      </div>

      <img
        src={project.image}
        alt={project.title}
        style={styles.projectImage}
      />

      {project.embedLink ? (
        <div style={styles.embedContainer}>
          <iframe
            allowFullScreen="allowfullscreen"
            scrolling="no"
            style={styles.embedIframe}
            src={project.embedLink}
          />
        </div>
      ) : (
        <div style={styles.experienceButtonContainer}>
          <a
            href={project.projectLink || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.experienceButton}
            onClick={(e) => {
              if (!project.projectLink || project.projectLink === "#") {
                e.preventDefault();
                alert("Project link coming soon!");
              }
            }}
          >
            Click Here to Experience the Project
          </a>
        </div>
      )}

      <div style={styles.projectTools}>
        <h3>Tools Used:</h3>
        <div style={styles.toolsList}>
          {project.tools.map((tool, i) => (
            <span key={i} style={styles.projectToolTag}>
              {tool}
            </span>
          ))}
        </div>
      </div>

      <div
        style={styles.projectContent}
        dangerouslySetInnerHTML={{ __html: project.detailedDescription }}
      />

      {project.gallery && project.gallery.length > 0 && (
        <div style={styles.gallerySection}>
          <h3 style={styles.galleryTitle}>Project Screenshots</h3>
          <div style={styles.gallery}>
            {project.gallery.map((image, index) => (
              <div key={index} style={styles.galleryItem}>
                <img
                  src={image}
                  alt={`${project.title} Screenshot ${index + 1}`}
                  style={styles.galleryImage}
                  onClick={() => onImageClick(image)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Featured Project Card Component
const FeaturedProjectCard = ({ project, onClick, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getStyles(isMobile);

  const featuredProjectItem = {
    background: "white",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: isHovered && !isMobile
      ? "0 15px 40px rgba(0,0,0,0.12)"
      : "0 6px 25px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    transform: isHovered && !isMobile ? "translateY(-8px)" : "translateY(0)",
  };

  const featuredProjectImage = {
    position: "relative",
    height: "200px",
    overflow: "hidden",
    background: "#f5f3f0",
  };

  const featuredProjectImg = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s ease",
    transform: isHovered && !isMobile ? "scale(1.03)" : "scale(1)",
  };

  const featuredProjectBadge = {
    position: "absolute",
    top: "15px",
    left: "15px",
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
    color: "white",
    padding: "0.5rem 1rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const featuredProjectContent = {
    padding: "1.5rem",
  };

  const featuredProjectTitle = {
    fontSize: isMobile ? "1.2rem" : "1.3rem",
    fontWeight: 600,
    color: COLORS.text,
    marginBottom: "0.8rem",
    lineHeight: 1.3,
  };

  const featuredProjectDescription = {
    color: COLORS.textLight,
    fontSize: isMobile ? "0.9rem" : "0.95rem",
    lineHeight: 1.6,
    marginBottom: "1.2rem",
  };

  const featuredProjectTools = {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
  };

  const featuredProjectToolTag = {
    background: "#f8f6f2",
    color: COLORS.primary,
    padding: "0.3rem 0.8rem",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: 500,
    border: `1px solid ${COLORS.primary}`,
  };

  return (
    <div
      style={featuredProjectItem}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onClick={onClick}
    >
      <div style={featuredProjectImage}>
        <img
          src={project.image}
          alt={project.title}
          style={featuredProjectImg}
        />
        <div style={featuredProjectBadge}>
          {project.badge}
        </div>
      </div>
      <div style={featuredProjectContent}>
        <h3 style={featuredProjectTitle}>{project.title}</h3>
        <p style={featuredProjectDescription}>{project.description}</p>
        <div style={featuredProjectTools}>
          {project.tools.slice(0, 3).map((tool, i) => (
            <span key={i} style={featuredProjectToolTag}>
              {tool}
            </span>
          ))}
          {project.tools.length > 3 && (
            <span style={featuredProjectToolTag}>
              +{project.tools.length - 3} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Other Work Card Component
const OtherWorkCard = ({ item, getTypeIcon, onClick, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  const styles = getStyles(isMobile);

  const otherWorkItem = {
    background: "white",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: isHovered && !isMobile
      ? "0 12px 30px rgba(0,0,0,0.12)"
      : "0 6px 25px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    transform: isHovered && !isMobile ? "translateY(-5px)" : "translateY(0)",
  };

  const otherWorkImage = {
    position: "relative",
    height: isMobile ? "180px" : "220px",
    overflow: "hidden",
    background: "#f5f3f0",
  };

  const otherWorkImg = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s ease",
    transform: isHovered && !isMobile ? "scale(1.05)" : "scale(1)",
  };

  const otherWorkOverlay = {
    position: "absolute",
    top: "15px",
    right: "15px",
    background: "rgba(0,0,0,0.8)",
    color: "white",
    padding: "0.5rem 1rem",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.8rem",
    fontWeight: 600,
  };

  const otherWorkContent = {
    padding: isMobile ? "1rem" : "1.5rem",
  };

  const otherWorkTitle = {
    fontSize: isMobile ? "1.1rem" : "1.3rem",
    fontWeight: 600,
    color: COLORS.text,
    marginBottom: "0.8rem",
    lineHeight: 1.3,
  };

  const otherWorkDescription = {
    color: COLORS.textLight,
    fontSize: isMobile ? "0.9rem" : "0.95rem",
    lineHeight: 1.6,
    marginBottom: "1.5rem",
  };

  const otherWorkMeta = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "1rem" : "0",
    justifyContent: isMobile ? "flex-start" : "space-between",
    alignItems: isMobile ? "flex-start" : "center",
  };

  const otherWorkCategory = {
    background: "#f8f6f2",
    color: COLORS.primary,
    padding: "0.4rem 1rem",
    borderRadius: "15px",
    fontSize: "0.8rem",
    fontWeight: 600,
  };

  const otherWorkLink = {
    color: isHovered && !isMobile ? COLORS.secondary : COLORS.primary,
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    transition: "color 0.3s ease",
  };

  return (
    <div
      style={otherWorkItem}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onClick={onClick}
    >
      <div style={otherWorkImage}>
        <img
          src={item.image}
          alt={item.title}
          style={otherWorkImg}
        />
        <div style={otherWorkOverlay}>
          <span style={{ fontSize: "1.2rem" }}>{getTypeIcon(item.type)}</span>
          <span style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
            {item.type.toUpperCase()}
          </span>
        </div>
      </div>
      <div style={otherWorkContent}>
        <h3 style={otherWorkTitle}>{item.title}</h3>
        <p style={otherWorkDescription}>{item.description}</p>
        <div style={otherWorkMeta}>
          <span style={otherWorkCategory}>{item.category}</span>
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            style={otherWorkLink}
            onClick={(e) => {
              e.stopPropagation();
              if (!item.link || item.link.includes("YOUR-")) {
                e.preventDefault();
                alert("Link coming soon!");
              }
            }}
          >
            View Work →
          </a>
        </div>
      </div>
    </div>
  );
};

// Other Work Page Component
const OtherWorkPage = ({ onNavigate }) => {
  const [selectedWork, setSelectedWork] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const isMobile = useIsMobile();
  const styles = getStyles(isMobile);
  
  const allCategories = [...new Set(OTHER_WORK.map((item) => item.category))];

  const filteredWork =
    selectedCategory === "All"
      ? OTHER_WORK
      : OTHER_WORK.filter((item) => item.category === selectedCategory);

  const getTypeIcon = (type) => {
    switch (type) {
      case "infographic":
        return "📊";
      case "video":
        return "🎥";
      case "document":
        return "📄";
      case "presentation":
        return "📑";
      case "training-module":
        return "🎯";
      default:
        return "📁";
    }
  };

  const handleWorkClick = (work) => {
    setSelectedWork(work);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (selectedWork) {
    return <WorkDetailView work={selectedWork} onClose={() => setSelectedWork(null)} isMobile={isMobile} />;
  }

  if (selectedProject) {
    return (
      <ProjectDetailView
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onImageClick={() => {}}
        isMobile={isMobile}
      />
    );
  }

  const categoryFilter = {
    display: "flex",
    justifyContent: "center",
    gap: isMobile ? "0.5rem" : "1rem",
    marginBottom: "3rem",
    flexWrap: "wrap",
    padding: isMobile ? "0 1rem" : "0",
  };

  const categoryButton = {
    background: "white",
    border: `2px solid ${COLORS.primary}`,
    color: COLORS.primary,
    padding: isMobile ? "0.6rem 1.2rem" : "0.8rem 1.5rem",
    borderRadius: "25px",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
  };

  const categoryButtonActive = {
    background: COLORS.primary,
    color: "white",
  };

  const portfolioSectionTitle = {
    fontSize: isMobile ? "1.8rem" : "2.2rem",
    color: COLORS.text,
    fontWeight: 300,
    marginBottom: "2rem",
    textAlign: "center",
  };

  const featuredProjectsGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(350px, 1fr))",
    gap: isMobile ? "1.5rem" : "2rem",
  };

  const otherWorkGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
    gap: isMobile ? "1.5rem" : "2rem",
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.mainTitle}>Portfolio</h1>
          <div style={styles.lineSeparator}></div>
          <p style={styles.subtitle}>
            Featured Projects • Training Modules • Infographics • Videos • Documents
          </p>
          <p style={styles.description}>
            A comprehensive collection of my professional work showcasing
            expertise in instructional design, visual design, and multimedia
            content creation.
          </p>
        </div>
      </header>

      <MobileNavigation 
        currentPage="otherWork"
        onNavigate={onNavigate}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isMobile={isMobile}
      />

      <main style={styles.main}>
        <div style={categoryFilter}>
          <button
            style={{
              ...categoryButton,
              ...(selectedCategory === "All" ? categoryButtonActive : {}),
            }}
            onClick={() => setSelectedCategory("All")}
          >
            All Work
          </button>
          {allCategories.map((category) => (
            <button
              key={category}
              style={{
                ...categoryButton,
                ...(selectedCategory === category ? categoryButtonActive : {}),
              }}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {selectedCategory === "All" && (
          <div style={{ marginBottom: "4rem" }}>
            <h2 style={portfolioSectionTitle}>Featured Projects</h2>
            <div style={featuredProjectsGrid}>
              {PROJECTS.map((project) => (
                <FeaturedProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => handleProjectClick(project)}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
        )}

        {filteredWork.length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <h2 style={portfolioSectionTitle}>
              {selectedCategory === "All" ? "Additional Work" : selectedCategory}
            </h2>
            <div style={otherWorkGrid}>
              {filteredWork.map((item) => (
                <OtherWorkCard
                  key={item.id}
                  item={item}
                  getTypeIcon={getTypeIcon}
                  onClick={() => handleWorkClick(item)}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Main Portfolio Component
const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentPage, setCurrentPage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const styles = getStyles(isMobile);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changePage = (page) => {
    setCurrentPage(page);
    setSelectedProject(null);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLinkedInConnect = () => {
    window.open(PERSONAL_INFO.linkedin, '_blank');
  };

  const handleContainerClick = () => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  if (selectedProject) {
    return (
      <>
        <ProjectDetailView
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onImageClick={setLightboxImage}
          isMobile={isMobile}
        />
        {lightboxImage && (
          <Lightbox 
            image={lightboxImage} 
            onClose={() => setLightboxImage(null)} 
            isMobile={isMobile}
          />
        )}
      </>
    );
  }

  if (currentPage === "otherWork") {
    return <OtherWorkPage onNavigate={changePage} />;
  }

  return (
    <div style={styles.container} onClick={handleContainerClick}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.mainTitle}>{PERSONAL_INFO.name}</h1>
          <div style={styles.lineSeparator}></div>
          <p style={styles.subtitle}>{PERSONAL_INFO.title}</p>
          <p style={styles.description}>{PERSONAL_INFO.description}</p>
        </div>
      </header>

      <MobileNavigation 
        currentPage={currentPage}
        onNavigate={changePage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isMobile={isMobile}
      />

      <main style={styles.main}>
        <section id="portfolio" style={styles.portfolioSection}>
          <div style={styles.portfolioHeader}>
            <h2 style={styles.sectionTitle}>Featured Projects</h2>
            <div style={styles.lineSeparator}></div>
            <p style={styles.sectionDescription}>
              Discover my latest e-learning and instructional design work,
              featuring innovative approaches to corporate training, game-based
              learning, and interactive skill development.
            </p>
          </div>

          <div style={styles.portfolioGrid}>
            {PROJECTS.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => handleProjectClick(project)}
                isMobile={isMobile}
              />
            ))}
          </div>
        </section>
      </main>

      <section id="about" style={styles.aboutSection}>
        <div style={styles.aboutContent}>
          <div style={styles.aboutText}>
            <h2 style={styles.sectionTitle}>About Me</h2>
            <div style={styles.lineSeparator}></div>
            
            <p style={styles.aboutParagraph}>
              I'm Taylor Thomas, an instructional designer with a background in science and education. I hold a Master's in Instructional and Learning Design Technology from UT Arlington and specialize in creating eLearning that sticks.
            </p>
            <p style={styles.aboutParagraph}>
              Whether it's gamified lessons or scenario-based training, I use tools like Articulate Storyline and Canva to make learning engaging and practical. I'm all about clarity, creativity, and building experiences that actually make a difference.
            </p>
          </div>
          <div style={styles.aboutImage}>
            <img
              src="/images/taylort.jpeg"
              alt="Taylor Thomas"
              style={styles.aboutImg}
            />
          </div>
        </div>

        <div style={styles.expertiseGrid}>
          {EXPERTISE.map((skill, index) => (
            <div key={index} style={styles.expertiseItem}>
              <h4 style={styles.expertiseTitle}>{skill.title}</h4>
              <p style={styles.expertiseDescription}>{skill.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" style={styles.contactSection}>
        <div style={styles.contactContent}>
          <h2 style={styles.contactTitle}>Ready to Transform Your Training?</h2>
          <div style={styles.lineSeparator}></div>
          <p style={styles.contactDescription}>
            Let's collaborate on creating learning experiences that your
            learners will actually love and remember. I'm excited to bring your
            training vision to life.
          </p>
          <button
            style={styles.contactButton}
            onClick={handleLinkedInConnect}
          >
            Let's Connect
          </button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;